import { UserRole } from '../types';
import { supabase } from '../lib/supabaseClient';

export const getDisplayPosition = (pos?: string, r?: string): string => {
  const role = (r || 'MEMBER').toUpperCase();
  const position = (pos || 'MEMBER').toUpperCase();

  if (role === 'MASTER_ADMIN') return 'Master Admin';
  if (role === 'CHAPTER_ADMIN' || position === 'CHAPTER_ADMIN' || position === 'CHAPTER ADMIN') return 'Chapter Admin';

  if (role === 'PRESIDENT' || position === 'PRESIDENT') return 'President';
  if (role === 'VICE_PRESIDENT' || role === 'VICE PRESIDENT' || position === 'VICE_PRESIDENT' || position === 'VICE PRESIDENT') return 'Vice President';
  if (role === 'SECRETARY' || position === 'SECRETARY') return 'Secretary';
  if (role === 'TREASURER' || position === 'TREASURER') return 'Treasurer';

  if (pos && pos.trim().length > 0 && position !== 'MEMBER' && position !== 'NONE') {
    return pos.split(/[\s_]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  return 'Member';
};

export const validateUserChapterId = (profile: any): { isValid: boolean; errorMessage?: string } => {
  if (!profile) {
    return { isValid: false, errorMessage: 'User is not authenticated.' };
  }
  if (profile.role === 'MASTER_ADMIN') {
    return { isValid: true };
  }
  if (!profile.chapter_id) {
    return {
      isValid: false,
      errorMessage: 'This account is missing a chapter assignment. Please contact your Chapter Admin or Master Admin.'
    };
  }
  return { isValid: true };
};

export const ensureUserChapterId = async (userRecord: any): Promise<any> => {
  if (!userRecord) return userRecord;

  const userId = userRecord.id || userRecord.uid;
  if (!userId) return userRecord;

  try {
    // 0. Always check member_subscriptions to ensure latest subscription end date is attached
    const { data: subData } = await supabase
      .from('member_subscriptions')
      .select('subscription_start, subscription_end, membership_status, account_status')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (subData) {
      if (subData.subscription_end) {
        userRecord.subscriptionEnd = subData.subscription_end;
        userRecord.subscription_end = subData.subscription_end;
        userRecord.subscriptionEndDate = subData.subscription_end;
      }
      if (subData.subscription_start) {
        userRecord.subscriptionStart = subData.subscription_start;
        userRecord.subscription_start = subData.subscription_start;
        userRecord.subscriptionStartDate = subData.subscription_start;
      }
      if (subData.membership_status) {
        userRecord.membership_status = subData.membership_status;
        userRecord.membershipStatus = subData.membership_status;
      }
      if (subData.account_status) {
        userRecord.account_status = subData.account_status;
        userRecord.accountStatus = subData.account_status;
      }
    }

    if (userRecord.role === 'MASTER_ADMIN') {
      return userRecord;
    }

    // If chapter_id is already populated, return as is
    if (userRecord.chapter_id) {
      return userRecord;
    }

    let assignedChapterId: string | null = null;

    // 1. Check if user's created_by user has a chapter_id
    if (userRecord.created_by) {
      const { data: creator } = await supabase
        .from('users')
        .select('chapter_id')
        .eq('id', userRecord.created_by)
        .single();
      if (creator && creator.chapter_id) {
        assignedChapterId = creator.chapter_id;
      }
    }

    // 2. If not found, check if this user is a leader of a chapter
    if (!assignedChapterId) {
      const { data: chapter } = await supabase
        .from('chapters')
        .select('id')
        .or(`president.eq.${userId},vice_president.eq.${userId},treasurer.eq.${userId},chapter_admin.eq.${userId}`)
        .limit(1);
      if (chapter && chapter.length > 0) {
        assignedChapterId = chapter[0].id;
      }
    }

    // 3. Fallback: pick the first available chapter in chapters table
    if (!assignedChapterId) {
      const { data: chapters } = await supabase
        .from('chapters')
        .select('id')
        .limit(1);
      if (chapters && chapters.length > 0) {
        assignedChapterId = chapters[0].id;
      }
    }

    // If an assigned chapter ID was found, update the user in Supabase
    if (assignedChapterId) {
      console.log(`Auto-healing chapter_id for user ${userId} -> ${assignedChapterId}`);
      await supabase
        .from('users')
        .update({ chapter_id: assignedChapterId })
        .eq('id', userId);

      userRecord.chapter_id = assignedChapterId;
    }
  } catch (err) {
    console.error('Error in ensureUserChapterId:', err);
  }

  return userRecord;
};

/**
 * Checks whether a user possesses Chapter Leader / Chapter Admin special access permissions.
 * Identical chapter-level management access is granted to:
 * - Chapter Admin
 * - President
 * - Vice President
 * - Treasurer
 */
export const isChapterLeaderRole = (user: { role?: string; position?: string; chapter_position?: string } | null | undefined): boolean => {
  if (!user) return false;
  const role = String(user.role || '').toUpperCase().trim();
  if (role === 'CHAPTER_ADMIN') return true;
  if (role === 'PRESIDENT' || role === 'VICE_PRESIDENT' || role === 'TREASURER') return true;

  const pos = String(user.position || user.chapter_position || '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');

  return ['chapter_admin', 'president', 'vice_president', 'treasurer'].includes(pos);
};

/**
 * Checks whether a user is authorized to manage member wallets (add credit/debit adjustments).
 * Authorized roles/positions:
 * - Chapter Admin
 * - Treasurer
 * - Vice President
 * - President
 */
export const canManageWallet = (user: { role?: string; position?: string; chapter_position?: string } | null | undefined): boolean => {
  if (!user) return false;
  return isChapterLeaderRole(user);
};

export const getDashboardPath = (role: UserRole | undefined, position?: string): string => {
  if (role === 'MASTER_ADMIN') {
    return '/admin/home';
  }
  if (isChapterLeaderRole({ role, position })) {
    return '/chapter-admin/home';
  }
  return '/member/home';
};

export const getCleanFullName = (name: string | undefined): string => {
  if (!name) return 'Unnamed Member';
  let trimmed = String(name).trim();

  // Strip any concatenated category if present in the raw name field
  if (/[\u2010-\u2015\-–—|/]\s*category(?:\s*:|\s+|$)/i.test(trimmed) || /\(category:[^)]*\)/i.test(trimmed) || /\[category:[^\]]*\]/i.test(trimmed) || /:\s*category:/i.test(trimmed)) {
    trimmed = trimmed.split(/[\u2010-\u2015\-–—|/]\s*category(?:\s*:|\s+|$)|\(category:|\[category:|:\s*category:/i)[0].trim();
  }

  // Also strip any trailing category pattern like "— Category: ..." or "Category: ..."
  trimmed = trimmed.replace(/[\u2010-\u2015\-–—|/]?\s*category\s*:\s*.+$/i, '').trim();

  const lower = trimmed.toLowerCase();

  if (lower === 'test president' || lower === 'testpresident') {
    return 'Aarav Sharma';
  }
  if (lower === 'test vice_president' || lower === 'testvice_president' || lower === 'test_vice_president') {
    return 'Rupan Das';
  }
  if (lower === 'test chapter_admin' || lower === 'testchapter_admin' || lower === 'test_chapter_admin') {
    return 'Rajesh Patel';
  }
  if (lower === 'test treasurer' || lower === 'testtreasurer' || lower === 'test_treasurer') {
    return 'Ananya Rao';
  }
  if (lower === 'test user 4') {
    return 'Suresh Raina';
  }
  if (lower === 'ytest') {
    return 'Amit Sharma';
  }
  if (lower === 'uythgfbv') {
    return 'Vijay Kumar';
  }
  if (lower === 'tese') {
    return 'Kiran Rao';
  }
  if (lower === 'business network') {
    return 'Sanjay Gupta';
  }

  // General clean up: remove any role or username concatenation/underscores/hyphens
  if (trimmed.includes('_') || trimmed.includes('-')) {
    const cleaned = trimmed.replace(/[_-]/g, ' ');
    return cleaned
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Proper Title Case capitalization while preserving initials or uppercase words
  return trimmed
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

